import { useIntl } from 'react-intl';

import { additionalDetailsSectionMessages } from './additional-details-section-messages';
import styles from './AdditionalDetailsSection.module.css';
import { notificationsSectionMessages } from '../../../freight-booking-wizard/components/notifications-section/notifications-section-messages';
import { NotificationTypePills } from '../../../freight-booking-wizard/components/notifications-section/NotificationTypePills';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { DetailSection } from '../detail-section/DetailSection';
import { MeasurementCell } from '../measurement-cell/MeasurementCell';

import type { InsuranceCommodityCategory, InsuranceItemCondition } from '../../../../types/freight-requests';
import type { LtlShipment } from '../../../../types/ltl';

const CATEGORY_MESSAGE_MAP: Record<InsuranceCommodityCategory, keyof typeof freightFormMessages> = {
  GeneralMerchandise: 'categoryGeneralMerchandise',
  Equipment: 'categoryEquipment',
  FoodBeverages: 'categoryFoodBeverages',
  ChemicalsPaint: 'categoryChemicalsPaint',
  Electronics: 'categoryElectronics',
  Construction: 'categoryConstruction',
  Furniture: 'categoryFurniture',
  Stonework: 'categoryStonework',
  FragileItems: 'categoryFragileItems',
  Perishables: 'categoryPerishables',
  Jewelry: 'categoryJewelry',
  MotorizedTransport: 'categoryMotorizedTransport',
  ArtAntiques: 'categoryArtAntiques',
  Explosives: 'categoryExplosives',
  LifeForms: 'categoryLifeForms',
  ControlledItems: 'categoryControlledItems',
  PharmaNonRefrigerated: 'categoryPharmaNonRefrigerated',
  PharmaRefrigerated: 'categoryPharmaRefrigerated',
};

const CONDITION_MESSAGE_MAP: Record<InsuranceItemCondition, keyof typeof freightFormMessages> = {
  New: 'conditionNew',
  Used: 'conditionUsed',
};

type AdditionalDetailsSectionProps = {
  shipment: LtlShipment;
  currencyCode?: string;
};

const DEFAULT_CURRENCY_CODE = 'USD';

export const AdditionalDetailsSection = ({ shipment, currencyCode = DEFAULT_CURRENCY_CODE }: AdditionalDetailsSectionProps) => {
  const intl = useIntl();

  const { insurance } = shipment;
  const hasInsurance = insurance?.isInsured === true;
  const hasHandlingCharge = Boolean(shipment.handlingCharge);
  const hasReferences = (shipment.references?.length ?? 0) > 0;
  const hasNotifications = (shipment.notifications?.length ?? 0) > 0;

  if (!hasInsurance && !hasHandlingCharge && !hasReferences && !hasNotifications) {
    return null;
  }

  return (
    <DetailSection testId='additional-details-section' title={intl.formatMessage(additionalDetailsSectionMessages.sectionTitle)}>
      <div className={styles.groups}>
        {hasInsurance && (
          <div className={styles.group}>
            <span className={styles.groupTitle}>{intl.formatMessage(additionalDetailsSectionMessages.insuranceTitle)}</span>
            <div className={styles.measurementRow}>
              <MeasurementCell
                label={intl.formatMessage(additionalDetailsSectionMessages.conditionOfGoods)}
                value={
                  CONDITION_MESSAGE_MAP[insurance.condition as InsuranceItemCondition]
                    ? intl.formatMessage(freightFormMessages[CONDITION_MESSAGE_MAP[insurance.condition as InsuranceItemCondition]])
                    : insurance.condition
                }
              />
              <MeasurementCell
                label={intl.formatMessage(additionalDetailsSectionMessages.category)}
                value={
                  CATEGORY_MESSAGE_MAP[insurance.commodityCategoryCode as InsuranceCommodityCategory]
                    ? intl.formatMessage(
                        freightFormMessages[CATEGORY_MESSAGE_MAP[insurance.commodityCategoryCode as InsuranceCommodityCategory]],
                      )
                    : insurance.commodityCategoryCode
                }
              />
              <MeasurementCell
                label={intl.formatMessage(additionalDetailsSectionMessages.insuredValue)}
                value={intl.formatNumber(insurance.declaredValue, { style: 'currency', currency: currencyCode })}
              />
              {insurance.marksNumbers && (
                <MeasurementCell label={intl.formatMessage(additionalDetailsSectionMessages.marksNumbers)} value={insurance.marksNumbers} />
              )}
            </div>
          </div>
        )}

        {shipment.handlingCharge && (
          <div className={styles.group}>
            <span className={styles.groupTitle}>{intl.formatMessage(additionalDetailsSectionMessages.handlingChargeTitle)}</span>
            <span className={styles.handlingChargeValue}>
              {intl.formatNumber(shipment.handlingCharge.value, { style: 'currency', currency: currencyCode })}
            </span>
          </div>
        )}

        {hasReferences && (
          <div className={styles.group}>
            <span className={styles.groupTitle}>{intl.formatMessage(additionalDetailsSectionMessages.referencesTitle)}</span>
            <div className={styles.referencesList}>
              {shipment.references?.map((ref) => (
                <div className={styles.referenceRow} key={`${ref.type}-${ref.value}`}>
                  <span className={styles.referenceType}>{ref.type}</span>
                  <span>{ref.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasNotifications && (
          <div className={styles.group}>
            <span className={styles.groupTitle}>{intl.formatMessage(notificationsSectionMessages.sectionTitle)}</span>
            <div className={styles.notificationsList}>
              {shipment.notifications?.map((notification) => (
                <div className={styles.notificationRow} key={notification.emails.join(',')}>
                  <span className={styles.notificationEmails}>{notification.emails.join(', ')}</span>
                  <NotificationTypePills types={notification.types} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DetailSection>
  );
};
